import React, { useEffect, useState } from 'react';
import { getProviderLinks, ProviderLink, unlinkProvider, OAuthProvider } from '../api/oauthApi';
import SocialSignIn from './SocialSignIn';
import { useAuth } from '../auth/AuthContext';

export default function AccountConnections() {
  const { logout } = useAuth();
  const [links, setLinks] = useState<ProviderLink[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setError(null);
    getProviderLinks().then((data) => {
      if (active) setLinks(data.links);
    }).catch((reason) => {
      if (active) setError(reason instanceof Error ? reason.message : 'Unable to load linked accounts.');
    });
    return () => { active = false; };
  }, [attempt]);

  const unlink = async (provider: OAuthProvider) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await unlinkProvider(provider);
      setLinks((current) => current?.filter((link) => link.provider !== provider) ?? null);
      setNotice('Sign-in provider unlinked. Your sessions were revoked; sign in again to continue.');
      await logout();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to unlink this account.');
    } finally { setBusy(false); }
  };

  return <section className="card" aria-label="Connected sign-in methods">
    <h2>Connected sign-in methods</h2>
    <p>A verified provider email can automatically link to your existing verified, active password account. Some older accounts may need to verify their email again or link explicitly after signing in. Apple Hide My Email uses a relay address, so it cannot match your underlying email; sign in to your existing account and link Apple here instead.</p>
    <p>The server will prevent removing your last usable sign-in method. Link another provider or set a password before removing it. Unlinking signs you out on all devices.</p>
    {!links && !error && <p role="status">Loading linked accounts…</p>}
    {links?.map((link) => <div key={link.provider}>
      <span>{link.provider === 'google' ? 'Google' : 'Apple'}{link.email ? ` — ${link.email}` : ''}</span>
      {' '}<button className="btn btn--ghost btn--sm" disabled={busy} onClick={() => unlink(link.provider)}>
        Unlink {link.provider === 'google' ? 'Google' : 'Apple'}
      </button>
    </div>)}
    {error && <p role="alert">{error}{' '}
      <button className="btn btn--ghost btn--sm" disabled={busy} onClick={() => setAttempt((value) => value + 1)}>Reload linked accounts</button>
    </p>}
    {notice && <p role="status">{notice}</p>}
    <SocialSignIn destination="/account" intent="link" disabled={!links || busy} linkedProviders={links?.map((link) => link.provider)} />
  </section>;
}
